<template>
  <v-card depressed>
    <v-card-text class="ma-0 pa-0">
      <div class="relative">
        <div ref="mapDiv" id="delivery-zone-map" style="width: 100%; height: 60vh"></div>
        <div v-if="drawReady" class="draw-controls">
          <button
            type="button"
            class="draw-control-btn"
            :class="{ active: currentMode === 'polygon' }"
            title="Draw a shape"
            @click="startDrawing"
          >
            <span class="draw-icon draw-icon-polygon" />
          </button>
          <button
            type="button"
            class="draw-control-btn"
            :class="{ active: currentMode !== 'polygon' }"
            title="Stop drawing"
            @click="stopDrawing"
          >
            <span class="draw-icon draw-icon-stop" />
          </button>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

const polygonStyles = {
  fillColor: ({ properties }) => properties.color || "#000000",
  fillOpacity: 0.4,
  outlineColor: "#000000",
  outlineWidth: 1,
  outlineOpacity: 0.5,
};

export default {
  props: ["drawable", "regions", "cancelHandler", "centerLocation"],
  data() {
    return {
      google: null,
      map: null,
      draw: null,
      drawReady: false,
      currentMode: "select",
      regionFeatureIds: [],
      adjuster: {
        zoom: 13,
        mapTypeControl: true,
        gestureHandling: "cooperative",
        streetViewControl: false,
        mapTypeControlOptions: {
          mapTypeIds: [],
        },
      },
      editIndex: 0,
      activeZone: {},
      projectionListener: null,
    };
  },
  watch: {
    drawable(value) {
      if (!this.drawReady) return;
      if (value) {
        this.startDrawing();
      } else {
        this.stopDrawing();
      }
    },
    regions() {
      if (!this.drawReady) return;
      this.resetMap();
      this.displayRegions();
    },
    centerLocation: {
      async handler() {
        await this.initMap();
      },
      deep: true,
    },
    cancelHandler() {
      if (!this.drawReady) return;
      this.resetMap();
      this.displayRegions();
    },
  },
  async mounted() {
    await this.initMap();
  },
  beforeUnmount() {
    this.destroyMap();
  },
  methods: {
    async initMap() {
      try {
        this.destroyMap();
        this.google = await this._gloader.load();
        const mapElement = this.$refs.mapDiv;
        if (!mapElement) return;

        this.map = new this.google.maps.Map(mapElement, {
          ...this.adjuster,
          center: this.centerLocation,
        });

        new this.google.maps.Marker({
          position: this.centerLocation,
          map: this.map,
        });

        this.projectionListener = this.map.addListener("projection_changed", () => {
          this.initTerraDraw();
        });
      } catch (err) {
        console.log(err);
      }
    },
    initTerraDraw() {
      if (this.draw) return;

      const adapter = new TerraDrawGoogleMapsAdapter({
        lib: this.google.maps,
        map: this.map,
        coordinatePrecision: 9,
      });

      this.draw = new TerraDraw({
        adapter,
        modes: [
          new TerraDrawPolygonMode({
            styles: polygonStyles,
          }),
          new TerraDrawSelectMode({
            flags: {
              polygon: {
                feature: {
                  draggable: true,
                  coordinates: {
                    midpoints: true,
                    draggable: true,
                    deletable: true,
                  },
                },
              },
            },
            styles: {
              selectedPolygonColor: "#000000",
              selectedPolygonFillOpacity: 0.4,
              selectedPolygonOutlineColor: "#000000",
              selectedPolygonOutlineWidth: 1,
            },
          }),
        ],
      });

      this.draw.start();

      this.draw.on("ready", () => {
        this.drawReady = true;
        this.stopDrawing();
        if (this.regions.length) {
          this.displayRegions();
          this.adjustCenterZoom();
        }
      });

      this.draw.on("finish", (id, context) => {
        if (context.action !== "draw" || context.mode !== "polygon") return;
        const feature = this.draw.getSnapshotFeature(id);
        if (!feature) return;

        const coordinates = this.fromGeoJsonCoordinates(feature.geometry.coordinates[0]);
        this.regionFeatureIds.push(id);
        this.stopDrawing();
        this.emitAddRegion({ coordinates });
      });

      this.draw.on("change", (ids, type, context) => {
        if (type !== "update" || context?.target !== "geometry") return;
        ids.forEach((featureId) => {
          const index = this.regionFeatureIds.indexOf(featureId);
          if (index === -1) return;
          const feature = this.draw.getSnapshotFeature(featureId);
          if (!feature) return;
          const coordinates = this.fromGeoJsonCoordinates(feature.geometry.coordinates[0]);
          this.emitEditRegion(coordinates, index);
        });
      });

      this.draw.on("select", (id) => {
        const index = this.regionFeatureIds.indexOf(id);
        if (index === -1) return;
        if (this.regions[index]?.isEditable) return;
        this.editIndex = index;
        this.$emit("selectZoneEmit", this.regions[this.editIndex].id);
      });
    },
    destroyMap() {
      if (this.projectionListener) {
        this.google?.maps?.event?.removeListener(this.projectionListener);
        this.projectionListener = null;
      }
      if (this.draw) {
        this.draw.stop();
        this.draw = null;
      }
      this.drawReady = false;
      this.regionFeatureIds = [];
      this.map = null;
    },
    resetMap() {
      if (!this.draw) return;
      this.draw.clear();
      this.regionFeatureIds = [];
    },
    displayRegions() {
      if (!this.draw || !this.regions.length) return;

      const features = this.regions
        .map((region, index) => this.toGeoJsonFeature(region, index))
        .filter(Boolean);

      if (!features.length) return;

      const results = this.draw.addFeatures(features);
      results.forEach((result, index) => {
        if (result.valid) {
          this.regionFeatureIds.push(result.id);
        } else {
          console.warn("Invalid region feature:", this.regions[index], result.reason);
        }
      });
    },
    toGeoJsonFeature(region, index) {
      const paths = region.deliveryPolygon;
      if (!paths?.length) return null;

      const ring = paths.map((vertex) => [
        Number(vertex[1].toFixed(9)),
        Number(vertex[0].toFixed(9)),
      ]);
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([...first]);
      }

      return {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [ring],
        },
        properties: {
          mode: "polygon",
          regionIndex: index,
          color: region.color || "#000000",
          selectable: !region.isEditable,
        },
      };
    },
    fromGeoJsonCoordinates(ring) {
      const coordinates = ring.map(([lng, lat]) => [lat, lng]);
      if (coordinates.length > 1) {
        const first = coordinates[0];
        const last = coordinates[coordinates.length - 1];
        if (first[0] === last[0] && first[1] === last[1]) {
          coordinates.pop();
        }
      }
      return coordinates;
    },
    startDrawing() {
      if (!this.drawReady) return;
      this.currentMode = "polygon";
      this.draw.setMode("polygon");
    },
    stopDrawing() {
      if (!this.drawReady) return;
      this.currentMode = "select";
      this.draw.setMode("select");
    },
    emitEditRegion(data, index) {
      this.$emit("editRegion", data, index);
    },
    emitAddRegion(data) {
      this.$emit("addRegion", data);
    },
    adjustCenterZoom() {
      if (!this.map || !this.regions.length) return;

      const bounds = new this.google.maps.LatLngBounds();
      this.regions.forEach((region) => {
        if (region.deliveryPolygon?.length) {
          region.deliveryPolygon.forEach((path) => {
            bounds.extend({ lat: path[0], lng: path[1] });
          });
        }
      });
      this.map.fitBounds(bounds);
    },
  },
};
</script>

<style>
div.gm-bundled-control-on-bottom {
  top: 95px !important;
  bottom: inherit !important;
}

.draw-controls {
  position: absolute;
  left: 10px;
  bottom: 24px;
  display: flex;
  z-index: 1;
}

.draw-control-btn {
  width: 40px;
  height: 40px;
  border: 0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
}

.draw-control-btn:first-child {
  border-radius: 5px 0 0 5px;
}

.draw-control-btn:last-child {
  border-radius: 0 5px 5px 0;
}

.draw-control-btn.active {
  background: #e8e8e8;
}

.draw-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
}

.draw-icon-polygon {
  background: center / contain no-repeat
    url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAAkCAYAAADsHujfAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAIeSURBVHgBzZgxbsIwFIZ/6IzUzixe2ejAnrFj4QS+AfQEbQ+A1BuQsRv0AqQ7Ax1hCgdAooKlW2rHdpuYJI5dE/WTfhHZsf1iv/dsA7hBmCKmA1PCNJdljUIyBmR1QMPGLPjAo9EoWa1WyXK5TIbDoTImgiMt2MMHBDMA3W43LTgejxgMBvzxk+kGDrRhDx8MnU7np+B0OqnHayYKhyW6gj13fKD9fo9er5cWTKdTbDYbVX/PNJG//IUvph0uAEG5s/aZAqaQKdbq5tJAb9FGMp2r3wjFHfIyqg3uLdrUl1LY4TXaxrLhDPakg3IDttttKm6QLD/YRA2BWOMd0zPsqYw2m6h5gXDEB6Z32FMVbW91O6EQU7iGOwQetgbloLUbVBjzAXO0FfIoGz7BDxEcoo7IRjH8oZaH2DRS1gfwQwCHD6NwzxllTGz7JBBWx/B72EmzKyz8I4SDQ9XA6B+8Qt+Y5vBLHwb/IGjmDEph8I/crsjl4wxaNg5KlruFC51BC1COfwuRXXO0YT6D8v2F4m8QqV2REYo0afHl4GcFbWmy4l80g5vfUNnHAgZry5w1kJ3EWl0Eu1kKUTMdEIhwrdoVg0yH2VkawzxLa/l+Hx4hEKlan6UZzvck/m6EfG4iuABUG0jNEpVGNX5HJji/y6TyfUe2geLXJ0pP7Xojl7uviRAiaZnyUw6Xu29dTKf2VzQEwT/5Q0cZY8pPKd/AIlOL/NU7fgAAAABJRU5ErkJggg==");
}

.draw-icon-stop {
  background: center / contain no-repeat
    url("data:image/svg+xml;utf8,<svg width='10' height='13' viewBox='0 0 27 30' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M1 25V1L25 15.7692L14.8462 16.6923L22.2308 25.9231L17.6154 28.6923L9.30769 18.5385L1 25Z' fill='black' stroke='black'/></svg>");
}
</style>
